import { getLocalTimeZone, now } from "@internationalized/date"
import { captureException } from "@sentry/react"

import { APIMode, BASE_API_URL } from "./config"
import { log } from "./logging"
import { WelcomeToken } from "./types/local"
import WelcomeTokenStorage from "./WelcomeTokenStorage"

type ResponseFormat = "json" | "blob" | "text" | "none"

export class FetchError extends Error {
  constructor(args: { msg: string; rsp: Response; qrLookupID?: string }) {
    super(args.msg)
    this.name = args.rsp.status.toString()
    this.response = args.rsp
    this.qrLookupID = args.qrLookupID
    Object.setPrototypeOf(this, FetchError.prototype) // https://stackoverflow.com/a/41429145/6444
  }

  response: Response
  qrLookupID?: string
}

interface GoOpts {
  apiMode?: APIMode // defaults to "gated"
  url: string
  options?: RequestInit
  responseType?: ResponseFormat
}

const DEFAULT_GO_OPTS: Omit<GoOpts, "url" | "apiMode"> & {
  contentType: string
  apiMode: APIMode
} = {
  options: {},
  apiMode: "gated",
  responseType: "json",
  contentType: "application/json",
}

async function go<T>(opts: GoOpts) {
  const { url, options, responseType, contentType, apiMode } = {
    ...DEFAULT_GO_OPTS,
    ...opts,
  }

  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": contentType,
  })

  const sessionData = WelcomeTokenStorage.getWelcomeToken()
  if (sessionData) {
    headers.set("Authorization", `Bearer ${sessionData.token}`)
  }

  const defaultOptions: RequestInit = {
    headers,
    cache: "no-cache",
  }

  const finalOptions: RequestInit = { ...defaultOptions, ...options }
  const start = now(getLocalTimeZone())
  try {
    log({
      level: "log",
      scope: "fetchRequest",
      message: "request_sent",
      flow: "misc",
      meta: {
        endpoint: url,
        timeStamp: start.toAbsoluteString(),
        qrLookupID: sessionData?.qrLookupID,
        token: sessionData?.token,
        body: opts.options?.body,
      },
      style: requestLogTitleStyle,
      group: { title: "Request Details" },
    })

    const rsp = await fetch(`${BASE_API_URL[apiMode]}${url}`, finalOptions)

    if (!rsp.ok) {
      const msg = await rsp.text()
      try {
        const errorRsp = JSON.parse(msg)
        if ("failReason" in errorRsp) {
          logError({
            error: errorRsp,
            sessionData,
            url,
            body: finalOptions.body,
          })
          return errorRsp as T
        }
        throw "Not a failReason"
      } catch {
        throw new FetchError({
          msg,
          rsp,
          qrLookupID: sessionData ? sessionData.qrLookupID : undefined,
        })
      }
    }

    switch (responseType) {
      case "blob":
        return (await rsp.blob()) as unknown as T
      case "text":
        return (await rsp.text()) as unknown as T
      default: {
        const data = await rsp.json()
        const end = now(getLocalTimeZone())
        log({
          level: "log",
          flow: "misc",
          scope: "fetchResponse",
          message: "response_received",
          meta: {
            endpoint: url,
            timeStamp: now(getLocalTimeZone()).toAbsoluteString(),
            duration: `${end.compare(start)}ms`,
            data: data,
          },
          style: requestLogTitleStyle,
          group: { title: "Request Response" },
        })

        return data as T
      }
    }
  } catch (error: unknown) {
    logError({ error, sessionData, url, body: finalOptions.body })

    if (error instanceof FetchError) {
      console.error("FetchError:", error.message, "Response:", error.response)
      throw error
    } else if (error instanceof Error) {
      console.error("Error:", error.message)
      throw error
    } else {
      console.error("Unknown error type:", error)
      throw new Error("Unknown error occurred")
    }
  }
}

function logError(args: {
  error: unknown
  url: string
  sessionData: WelcomeToken | "" | null
  body: BodyInit | null | undefined
}) {
  let body: string | null = null
  try {
    if (typeof args.body === "string") {
      const parsed = JSON.parse(args.body)
      body = parsed
    }
  } catch {
    console.error("Error parsing body")
  }

  captureException(args.error, {
    extra: {
      endpoint: args.url,
      token: args.sessionData,
      body: body,
    },
  })
}

interface BaseOpts {
  url: string
  apiMode?: APIMode
}

type GetOpts = BaseOpts

export async function get<T>(opts: GetOpts): Promise<T> {
  return await go<T>({
    ...opts,
    options: { method: "GET" },
    responseType: "json",
  })
}

interface PostOpts extends BaseOpts {
  data?: any
}

export async function post<T>(opts: PostOpts): Promise<T> {
  return await go<T>({
    ...opts,
    options: {
      method: "POST",
      body: JSON.stringify(opts.data),
    },
  })
}

interface PutOpts extends BaseOpts {
  data?: any
}

export async function put<T>(opts: PutOpts): Promise<T> {
  return await go<T>({
    ...opts,
    options: {
      method: "PUT",
      body: JSON.stringify(opts.data),
    },
  })
}

type DelOpts = BaseOpts

export async function del<T>(opts: DelOpts): Promise<T> {
  return await go<T>({
    ...opts,
    options: {
      method: "DELETE",
    },
    responseType: "none", // Currently we are expecting nothing returned by upload calls
  })
}

const requestLogTitleStyle = [
  "color: #A78BFA", // violet-400
  "font-weight: bold",
].join(";")
